import { Injectable } from '@angular/core';
import { firstValueFrom, Subject } from 'rxjs';
import { HttpBackendService } from 'src/app/core/backend-services/http/http.backend-service';
import { SocketIoService } from 'src/app/core/services/socket-io/socket-io.service';

export interface ChatMessage {
  id: number;
  message: string;
  channel: string;
  createdAt: string;
  userId: number;
  username: string;
  profilePicture?: string;
  smallProfilePicture?: string;
  premiumBadge?: boolean; // Stammgast crown
  isGuest?: boolean; // guest account (backend omits it for registered users)
}

export interface ChatResponse {
  messages: ChatMessage[];
  channel: string;
  pagination: {
    limit: number;
    offset: number;
  };
}

export type ChatChannel = 'LOBBY' | 'SPIEL_TALK' | 'TURNIERE';

@Injectable({
  providedIn: 'root',
})
export class ChatBackendService {
  private readonly BASE_ADDRESS = '/chat';

  // Real-time chat message stream
  private chatMessageSource = new Subject<{
    message: ChatMessage;
    channel: ChatChannel;
  }>();
  public chatMessage$ = this.chatMessageSource.asObservable();

  /**
   * @description constructor of ChatBackendService
   * @param {HttpBackendService} http - http backend service
   * @param {SocketIoService} socketIO - socket io service for real-time updates
   */
  constructor(
    private http: HttpBackendService,
    private socketIO: SocketIoService
  ) {
    this.initializeSocketListeners();
  }

  /**
   * @description initializes socket listeners for real-time chat
   */
  private initializeSocketListeners(): void {
    // Listen for incoming chat messages
    this.socketIO.on('chatMsg', (data: any) => {
      const chatMessage: ChatMessage = {
        id: Date.now(), // Temporary ID until proper backend integration
        message: data.data.msg,
        channel: data.data.channel || 'LOBBY',
        createdAt: new Date().toISOString(),
        userId: data.data.user.userID,
        username: data.data.user.username,
        profilePicture: data.data.user.profilePicture,
        smallProfilePicture: data.data.user.smallProfilePicture,
        premiumBadge: data.data.user.premiumBadge || undefined,
        isGuest: data.data.user.isGuest || undefined,
      };

      this.chatMessageSource.next({
        message: chatMessage,
        channel: data.data.channel || 'LOBBY',
      });
    });
  }

  /**
   * @description sends a message to a chat channel using socket.io for real-time delivery
   * @param {string} message - the message to send
   * @param {ChatChannel} channel - the channel to send to (optional, defaults to LOBBY)
   * @returns {Promise<any>}
   */
  public async sendMessage(
    message: string,
    channel: ChatChannel = 'LOBBY'
  ): Promise<any> {
    if (!message.trim()) return;

    try {
      // Send via socket.io for real-time delivery
      await this.socketIO.post('chat/send', {
        message: message.trim(),
        channel,
        recipient: channel, // For compatibility with existing backend
      });
    } catch (error) {
      console.warn(
        'Socket chat message send failed, falling back to HTTP:',
        error
      );
      // Fallback to HTTP if socket fails
      return firstValueFrom(
        this.http.post<any>(`${this.BASE_ADDRESS}/send`, {
          message: message.trim(),
          channel,
        })
      );
    }
  }

  /**
   * @description gets messages from a chat channel
   * @param {ChatChannel} channel - the channel to get messages from
   * @param {number} limit - limit for number of messages (default 50, max 100)
   * @param {number} offset - offset for pagination (default 0)
   * @param {string} before - get messages before this timestamp (optional)
   * @param {string} after - get messages after this timestamp (optional)
   * @returns {Promise<ChatResponse>}
   */
  public getMessages(
    channel: ChatChannel,
    limit: number = 50,
    offset: number = 0,
    before?: string,
    after?: string
  ): Promise<ChatResponse> {
    let url = `${this.BASE_ADDRESS}/messages/${channel}?limit=${limit}&offset=${offset}`;

    if (before) {
      url += `&before=${before}`;
    }

    if (after) {
      url += `&after=${after}`;
    }

    return firstValueFrom(this.http.get<ChatResponse>(url));
  }

  /**
   * @description gets recent messages from a chat channel
   * @param {ChatChannel} channel - the channel to get messages from
   * @param {number} limit - limit for number of messages (default 20)
   * @returns {Promise<ChatResponse>}
   */
  public getRecentMessages(
    channel: ChatChannel,
    limit: number = 20
  ): Promise<ChatResponse> {
    return this.getMessages(channel, limit, 0);
  }
}
